/**
 * D6-REVIEW-REACH-1 — /gates/:submissionId/review
 *
 * The deep link a named gate approver opens from an email. They may be an
 * external person on a MEMBER seat with NO workspace membership, so this page
 * assumes NOTHING about workspace context: it sends no workspace id, and the
 * server resolves submission -> project -> workspace from the persisted row.
 *
 * Mounted outside DashboardLayout (which is workspace-shaped) and inside
 * AppAuthenticatedChrome, like /onboarding.
 *
 * ⚠️ RULING A3 (founder, 2026-09-12) BINDS THIS PAGE. A gate is a HUMAN
 * decision point; the system does not evaluate the evidence. So: no verdict,
 * no "3 of 5 complete", no per-criterion EVIDENCED/ASSERTED badge, and the
 * decision controls are NOT gated on completeness. Evidence renders as a
 * separate list. No mapping between criteria and evidence is claimed —
 * evidence attaches to the SUBMISSION and carries no criterion reference.
 *
 * ⚠️ The server deliberately does not send checklist_snapshot.answered, and
 * this page must never reconstruct it, count it, or derive completeness from
 * anything on the payload. That rebuilds the signal A3 removed.
 *
 * ⚠️ AUTHORIZATION IS THE SERVER'S. The affordance binds to `canDecide` and
 * nothing else — never to a role, never to a locally computed guess. A 200
 * does NOT mean the caller may act: ALREADY_DECIDED and NOT_ACTIVE_STEP also
 * return 200 with canDecide:false so a returning approver sees an honest
 * receipt instead of a 403.
 *
 * MORE-INFORMATION-FE-1: a third action asks a REQUIRED question (G-R11).
 * G-R8 — non-punitive: timeline renders MORE_INFORMATION as a question, never
 * as a rejection.
 *
 * GATE-DECISION-FORM-1: tick boxes bind to `criteriaSnapshot` (#1099), not
 * live `criteria`. null ≠ []. Ticks are the approver's input — never a system
 * verdict — and all three actions stay available with zero ticked (G-R4).
 * Every action sends `attestations`: the exact ticked snapshot text.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle2, XCircle, HelpCircle, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

import {
  getGateReview,
  getGateReadiness,
  approveGateSubmission,
  rejectGateSubmission,
  requestMoreInformation,
  isBlankMoreInformationQuestion,
  GATE_MORE_INFORMATION_QUESTION_REQUIRED,
  type GateReview,
  type GateReviewPriorAttempt,
  type GateReadiness,
} from '@/features/phase-gates/phaseGates.api';
import { GateReadinessSection } from '@/features/phase-gates/GateReadinessSection';
import { GateApprovalStatus } from '@/features/phase-gates/GateApprovalStatus';
import { useWorkspaceStore } from '@/state/workspace.store';
import { useAuth } from '@/state/AuthContext';
import { copyForCannotApproveReason } from '@/features/phase-gates/gateCallerApprovalCopy';
import { listWorkspaceMembers } from '@/features/workspaces/workspace.api';

function formatAttemptDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** ATTEMPT-HISTORY-1. Nothing when this review has no earlier submissions. */
export function EarlierAttempts({
  attempts,
}: {
  attempts: GateReviewPriorAttempt[] | null | undefined;
}) {
  if (!attempts?.length) return null;
  return (
    <section className="mt-6" data-testid="earlier-attempts">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
        Earlier attempts
      </h2>
      <ul className="mt-2 space-y-2">
        {attempts.map((attempt) => (
          <li key={attempt.submissionId}>
            <a
              href={`/gates/${attempt.submissionId}/review`}
              className="text-sm text-indigo-700 hover:underline"
            >
              Attempt {attempt.attemptNumber} · {attempt.outcome} · {attempt.decidedBy ?? '—'} ·{' '}
              {formatAttemptDate(attempt.decidedAt)}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function evidenceLabel(item: { restricted?: boolean; name: string | null }): string {
  if (item.restricted || item.name == null || item.name.trim() === '') return 'Restricted';
  return item.name;
}

export default function GateReviewPage() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const { user } = useAuth();

  const [review, setReview] = useState<GateReview | null>(null);
  const [readiness, setReadiness] = useState<GateReadiness | null>(null);
  /** GATE-REVIEW-WS-1: a failed load is shown and retryable, never a blank. */
  const [readinessState, setReadinessState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [readinessAttempt, setReadinessAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  /** Required question for Ask for information — separate from the rejection reason. */
  const [question, setQuestion] = useState('');
  const [questionError, setQuestionError] = useState<string | null>(null);
  /** Exact snapshot text the approver has ticked. Order follows the snapshot. */
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    const workspaceId = review?.workspaceId;
    if (!workspaceId) return;
    let cancelled = false;
    void listWorkspaceMembers(workspaceId)
      .then((rows) => {
        if (cancelled) return;
        const next = new Map<string, string>();
        for (const row of rows ?? []) {
          const id = String(row.userId || row.user?.id || '');
          if (!id) continue;
          const email = row.user?.email || row.email || '';
          const name = [row.user?.firstName, row.user?.lastName].filter(Boolean).join(' ').trim()
            || row.user?.name
            || row.name
            || '';
          next.set(id, name && name !== email ? name : email || 'Approver');
        }
        setPeople(next);
      })
      .catch(() => {
        if (!cancelled) setPeople(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [review?.workspaceId]);

  const actorLabel = (userId: string | null | undefined): string => {
    if (!userId) return 'Approver';
    return people.get(userId) || 'Approver';
  };

  const load = useCallback(async () => {
    if (!submissionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getGateReview(submissionId);
      setReview(data);
      setTicked(new Set());
      // The decision routes still require an explicit workspace, and this
      // caller may have none. Adopt the workspace the SERVER just resolved and
      // authorised us for on this gate. This is not the path-derived guess in
      // RequireWorkspace.tsx: the id arrives in an authenticated response from
      // a route that already proved this caller may act here.
      if (data.workspaceId) setActiveWorkspace(data.workspaceId);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setError('You are not an approver for this gate.');
      } else if (status === 404) {
        setError('This gate could not be found.');
      } else {
        setError(code ?? 'Could not load this gate.');
      }
    } finally {
      setLoading(false);
    }
  }, [submissionId, setActiveWorkspace]);

  useEffect(() => {
    void load();
  }, [load]);

  // GATE-REVIEW-WS-1: readiness waits for the review, then sends the workspace
  // the SERVER resolved for this gate. Fired in parallel on mount it met an
  // empty workspace store (a named approver who is not a member), the client
  // guard threw WORKSPACE_REQUIRED before any request left, and the catch
  // rendered nothing.
  const reviewSubmissionId = review?.submissionId ?? null;
  const reviewWorkspaceId = review?.workspaceId ?? null;
  useEffect(() => {
    if (!submissionId || typeof getGateReadiness !== 'function') {
      setReadiness(null);
      setReadinessState('idle');
      return;
    }
    if (!reviewSubmissionId) return;
    let cancelled = false;
    setReadinessState('loading');
    void getGateReadiness(submissionId, reviewWorkspaceId)
      .then((body) => {
        if (cancelled) return;
        const ok = !!body && Array.isArray(body.items);
        setReadiness(ok ? body : null);
        setReadinessState(ok ? 'ready' : 'error');
      })
      .catch(() => {
        if (cancelled) return;
        setReadiness(null);
        setReadinessState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, reviewSubmissionId, reviewWorkspaceId, readinessAttempt]);

  /**
   * Exact ticked text from the frozen snapshot, in snapshot order.
   * When there is no snapshot (null) or an empty one, this is [].
   */
  const selectedAttestations = (): string[] => {
    const snapshot = review?.criteriaSnapshot;
    if (!snapshot || snapshot.length === 0) return [];
    return snapshot.filter((text) => ticked.has(text));
  };

  const toggleTick = (text: string) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(text)) next.delete(text);
      else next.add(text);
      return next;
    });
  };

  const decide = async (kind: 'approve' | 'reject') => {
    if (!submissionId || !review?.canDecide) return;
    if (kind === 'reject' && note.trim().length === 0) return;
    setBusy(true);
    setQuestionError(null);
    const attestations = selectedAttestations();
    try {
      if (kind === 'approve') {
        await approveGateSubmission(submissionId, note || undefined, attestations);
        toast.success('Gate approved.');
      } else {
        await rejectGateSubmission(submissionId, note.trim(), attestations);
        toast.success('Gate rejected.');
      }
      setNote('');
      setQuestion('');
      setTicked(new Set());
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Decision failed.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const askForInformation = async () => {
    if (!submissionId || !review?.canDecide) return;
    if (isBlankMoreInformationQuestion(question)) {
      setQuestionError(GATE_MORE_INFORMATION_QUESTION_REQUIRED);
      return;
    }
    setBusy(true);
    setQuestionError(null);
    const attestations = selectedAttestations();
    try {
      // Extend #1100's call — same route, shared body with attestations.
      await requestMoreInformation(submissionId, question, attestations);
      toast.success('Requested changes.');
      setNote('');
      setQuestion('');
      setTicked(new Set());
      await load();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === GATE_MORE_INFORMATION_QUESTION_REQUIRED) {
        setQuestionError(GATE_MORE_INFORMATION_QUESTION_REQUIRED);
        toast.error(GATE_MORE_INFORMATION_QUESTION_REQUIRED);
      } else {
        const msg = err instanceof Error ? err.message : 'Request failed.';
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const questionBlank = isBlankMoreInformationQuestion(question);
  const rejectReasonBlank = note.trim().length === 0;

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl p-8" data-testid="gate-review-loading">
        <p className="text-sm text-gray-600">Loading gate…</p>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="mx-auto max-w-2xl p-8" data-testid="gate-review-error">
        <h1 className="text-lg font-semibold text-gray-900">Gate review</h1>
        <p className="mt-2 text-sm text-gray-700" role="status">
          {error ?? 'Could not load this gate.'}
        </p>
      </div>
    );
  }

  const yourDecisionLabel = (() => {
    const d = review.yourDecision?.decision;
    if (!d) return null;
    if (d === 'MORE_INFORMATION') return 'requested changes on';
    return `${d.toLowerCase()} this`;
  })();

  // #1099: null and [] are different. Never coalesce for render decisions.
  const snapshot = review.criteriaSnapshot;
  const liveCriteria = review.criteria;
  const changeRequests = (review.timeline ?? []).filter(
    (entry) => entry.decision === 'MORE_INFORMATION',
  );

  return (
    <div className="mx-auto max-w-2xl p-8" data-testid="gate-review-page">
      <header className="flex items-center gap-2 border-b border-gray-200 pb-4">
        <Shield className="h-5 w-5 text-indigo-600" />
        <div>
          <h1
            className="text-lg font-semibold text-gray-900"
            data-testid="gate-review-gate-name"
          >
            {review.gateName ?? 'Gate'}
          </h1>
          <p className="text-sm text-gray-600" data-testid="gate-review-project-name">
            {review.projectName ?? 'Project'}
          </p>
        </div>
      </header>

      <EarlierAttempts attempts={review.priorAttempts} />

      <section className="mt-6" data-testid="gate-review-snapshot-summary">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Snapshot
        </h2>
        {snapshot === null ? (
          <p className="mt-2 text-sm text-gray-600">
            No snapshot was recorded for this attempt.
          </p>
        ) : snapshot.length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">
            An empty checklist was recorded.
          </p>
        ) : (
          <ul className="mt-2 space-y-1">
            {snapshot.map((text, i) => (
              <li
                key={`snap-${i}-${text}`}
                className="text-sm text-gray-900"
                data-testid="gate-review-snapshot-item"
              >
                {text}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        Criteria — GATE-DECISION-FORM-1 / #1099
        · snapshot non-empty → tickable (approver input; A3: not a verdict)
        · snapshot [] → nothing to tick (actions still available)
        · snapshot null → no ticks; live list read-only if non-empty, else nothing
      */}
      {snapshot !== null && snapshot.length > 0 ? (
        <section className="mt-6" data-testid="gate-review-criteria">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Criteria
          </h2>
          <ul className="mt-2 space-y-2">
            {snapshot.map((text, i) => (
              <li key={`${i}-${text}`} className="flex items-start gap-2 text-sm text-gray-900">
                <input
                  type="checkbox"
                  checked={ticked.has(text)}
                  onChange={() => toggleTick(text)}
                  disabled={!review.canDecide || busy}
                  data-testid="gate-review-attestation-tick"
                  className="mt-0.5"
                  aria-label={text}
                />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : snapshot === null && liveCriteria.length > 0 ? (
        <section
          className="mt-6"
          data-testid="gate-review-criteria-unrecorded"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Criteria
          </h2>
          <p className="mt-1 text-xs text-gray-600">
            Not recorded at submission. Live definition shown read-only.
          </p>
          <ul className="mt-2 space-y-1">
            {liveCriteria.map((c, i) => (
              <li
                key={`${i}-${c}`}
                className="text-sm text-gray-900"
                data-testid="gate-review-criterion"
              >
                {c}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Evidence — a SEPARATE list. No claimed mapping to criteria (A3). */}
      <section className="mt-6" data-testid="gate-review-evidence">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Attached evidence
        </h2>
        {review.evidence.length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">Nothing attached.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {review.evidence.map((e) => (
              <li
                key={e.id}
                className="flex items-center gap-2 text-sm text-gray-900"
                data-testid="gate-review-evidence-item"
              >
                <Paperclip className="h-3 w-3 text-gray-500" />
                {evidenceLabel(e)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <GateApprovalStatus submissionId={review.submissionId} />

      {review.resolvedNotDone && review.resolvedNotDone.length > 0 ? (
        <section className="mt-6" data-testid="gate-review-resolved-not-done">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Closed without completing
          </h2>
          <ul className="mt-2 space-y-2">
            {review.resolvedNotDone.map((task) => (
              <li
                key={task.taskId}
                className="text-sm text-gray-900"
                data-testid="gate-review-resolved-not-done-item"
              >
                <span className="font-medium">{task.title}</span>
                <span className="text-gray-600">
                  {' '}
                  ·{' '}
                  {task.closureKind === 'CANCELLED'
                    ? 'Canceled'
                    : task.closureKind === 'NOT_APPLICABLE'
                      ? 'Not applicable'
                      : 'Deferred to the next phase'}
                </span>
                <p className="text-gray-700">{task.reason}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The caller's OWN decision, when they have already made one. */}
      {review.yourDecision ? (
        <section
          className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-3"
          data-testid="gate-review-your-decision"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Your decision
          </h2>
          <p className="mt-1 text-sm text-gray-900">
            You {yourDecisionLabel} gate
            {review.yourDecision.stepName
              ? ` at “${
                  /^admin approval$/i.test(review.yourDecision.stepName)
                    ? `Approval by ${actorLabel(
                        review.timeline?.find(
                          (entry) =>
                            entry.stepId === review.yourDecision?.stepId &&
                            entry.decision === review.yourDecision?.decision,
                        )?.decidedByUserId,
                      )}`
                    : review.yourDecision.stepName
                }”`
              : ''}
            .
          </p>
          {review.yourDecision.decision === 'MORE_INFORMATION' &&
          review.yourDecision.note ? (
            <p
              className="mt-1 text-sm text-gray-700"
              data-testid="gate-review-your-question"
            >
              Your question: {review.yourDecision.note}
            </p>
          ) : review.yourDecision.note ? (
            <p className="mt-1 text-sm text-gray-700">
              Your note: {review.yourDecision.note}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* Decision timeline — every recorded decision (#1043). A3: no counts.
          MORE_INFORMATION note is a QUESTION (G-R8) — never styled as rejection. */}
      <section className="mt-6" data-testid="gate-review-timeline">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Decision timeline
        </h2>
        {(review.timeline ?? []).length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">No decisions yet</p>
        ) : (
          <ol className="mt-2 space-y-3">
            {(review.timeline ?? []).map((entry) => {
              const isQuestion = entry.decision === 'MORE_INFORMATION';
              return (
                <li
                  key={entry.id}
                  className="rounded border border-gray-200 p-3 text-sm text-gray-900"
                  data-testid="gate-review-timeline-entry"
                  data-decision={entry.decision}
                >
                  <p className="font-medium">
                    {/^admin approval$/i.test(entry.stepName ?? '') &&
                    actorLabel(entry.decidedByUserId) !== 'Approver'
                      ? `Approval by ${actorLabel(entry.decidedByUserId)}`
                      : (entry.stepName ?? 'Step')}
                    {entry.stepOrder != null ? ` · step ${entry.stepOrder}` : ''}
                  </p>
                  <p className="mt-1 text-gray-800" data-testid="gate-review-decision-who">
                    Who: {actorLabel(entry.decidedByUserId)}
                  </p>
                  <p className="mt-1 text-gray-800" data-testid="gate-review-decision-outcome">
                    Outcome:{' '}
                    {entry.decision === 'REJECTED' ? (
                      <>Rejected by {actorLabel(entry.decidedByUserId)}</>
                    ) : isQuestion ? (
                      <span data-testid="gate-review-timeline-more-information">
                        Request changes
                      </span>
                    ) : (
                      entry.decision
                    )}
                  </p>
                  <p className="mt-1 text-gray-800" data-testid="gate-review-decision-when">
                    When: {entry.decidedAt || '—'}
                  </p>
                  <p className="mt-1" data-testid="gate-review-decision-rationale">
                    Rationale:{' '}
                    {isQuestion && entry.note ? (
                      <span
                        className="text-gray-700"
                        data-testid="gate-review-timeline-question"
                      >
                        Question: {entry.note}
                        {entry.response ? (
                          <span
                            className="mt-1 block"
                            data-testid="gate-review-timeline-response"
                          >
                            {entry.response}
                          </span>
                        ) : null}
                      </span>
                    ) : entry.note ? (
                      <span className="text-gray-700">{entry.note}</span>
                    ) : (
                      <span
                        className="text-gray-400"
                        data-testid="gate-review-timeline-note-absent"
                      >
                        ABSENT
                      </span>
                    )}
                  </p>
                  {entry.attestations.length > 0 ? (
                    <ul
                      className="mt-2 space-y-0.5 text-gray-700"
                      data-testid="gate-review-timeline-attestations"
                    >
                      {entry.attestations.map((text, i) => (
                        <li key={`${entry.id}-att-${i}`}>{text}</li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {changeRequests.length > 0 ? (
        <section className="mt-6" data-testid="gate-review-request-changes">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Request changes
          </h2>
          <ul className="mt-2 space-y-3">
            {changeRequests.map((entry) => (
              <li
                key={entry.id}
                className="rounded border border-gray-200 p-3 text-sm text-gray-900"
                data-testid="gate-review-request-changes-item"
              >
                <p>Who: {actorLabel(entry.decidedByUserId)}</p>
                <p className="mt-1">When: {entry.decidedAt || '—'}</p>
                <p className="mt-1">Question: {entry.note ?? '—'}</p>
                {entry.response ? (
                  <p
                    className="mt-1"
                    data-testid="gate-review-request-changes-response"
                  >
                    Response: {entry.response}
                  </p>
                ) : (
                  <p className="mt-1 text-gray-600">No response recorded.</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {readinessState === 'ready' && readiness ? <GateReadinessSection readiness={readiness} /> : null}
      {readinessState === 'loading' ? (
        <p className="mt-6 text-sm text-gray-600" data-testid="gate-readiness-loading">
          Loading readiness…
        </p>
      ) : null}
      {readinessState === 'error' ? (
        <section
          className="mt-6 rounded border border-gray-300 p-3 text-sm text-gray-900"
          data-testid="gate-readiness-error"
          role="alert"
        >
          <p>Readiness could not be loaded.</p>
          <button
            type="button"
            className="mt-2 rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50"
            onClick={() => setReadinessAttempt((n) => n + 1)}
          >
            Retry
          </button>
        </section>
      ) : null}

      {/* Decision controls — bound to the SERVER's canDecide, nothing else.
          G-R4: available with zero items ticked. */}
      {review.canDecide ? (
        <section className="mt-6 space-y-3" data-testid="gate-review-decision">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Decision
          </h2>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for rejection (required)"
            aria-label="Reason for rejection"
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            data-testid="gate-review-decision-note"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void decide('approve')}
              disabled={busy}
              data-testid="gate-review-approve-btn"
              className="flex items-center gap-1 rounded bg-green-600 px-3 py-1 text-xs text-white hover:bg-green-700 disabled:bg-neutral-300"
            >
              <CheckCircle2 className="h-3 w-3" /> Approve
            </button>
            <button
              type="button"
              onClick={() => void decide('reject')}
              disabled={busy || rejectReasonBlank}
              title={
                rejectReasonBlank ? 'A note is required to reject.' : undefined
              }
              data-testid="gate-review-reject-btn"
              className="flex items-center gap-1 rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
            >
              <XCircle className="h-3 w-3" /> Reject
            </button>
          </div>

          {/* MORE-INFORMATION-FE-1 — third action; non-punitive (not red). */}
          <div
            className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3"
            data-testid="gate-review-ask-information"
          >
            <label
              htmlFor="gate-review-question"
              className="block text-xs font-medium text-gray-700"
            >
              What should change? (required to request changes)
            </label>
            <textarea
              id="gate-review-question"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                setQuestionError(null);
              }}
              placeholder="What do you need clarified?"
              rows={2}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              data-testid="gate-review-question-input"
            />
            {questionError ? (
              <p
                className="text-xs text-red-700"
                role="alert"
                data-testid="gate-review-question-error"
              >
                {questionError}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => void askForInformation()}
              disabled={busy || questionBlank}
              data-testid="gate-review-ask-information-btn"
              className="flex items-center gap-1 rounded border border-gray-400 bg-white px-3 py-1 text-xs text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <HelpCircle className="h-3 w-3" /> Request changes
            </button>
          </div>
        </section>
      ) : (
        <p
          className="mt-6 text-sm text-gray-700"
          role="status"
          data-testid="gate-review-no-decision"
        >
          {review.yourDecision
            ? 'This gate is recorded and needs nothing further from you.'
            : review.submittedByUserId &&
                user?.id &&
                review.submittedByUserId === user.id
              ? copyForCannotApproveReason('SELF_APPROVAL_NOT_PERMITTED')
              : review.cannotDecideReason === 'SELF_APPROVAL_NOT_PERMITTED'
                ? copyForCannotApproveReason('SELF_APPROVAL_NOT_PERMITTED')
                : 'This gate is not awaiting a decision from you.'}
        </p>
      )}
    </div>
  );
}
